import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CapitalRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentValue: number;
  onRequestComplete: () => void;
}

const CapitalRequestModal: React.FC<CapitalRequestModalProps> = ({
  isOpen,
  onClose,
  userId,
  currentValue,
  onRequestComplete,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestType, setRequestType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [amount, setAmount] = useState('');

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount greater than 0.",
        variant: "destructive",
      });
      return;
    }

    if (requestType === 'withdrawal' && numAmount > currentValue) {
      toast({
        title: "Invalid Amount",
        description: "Withdrawal amount cannot exceed your current account value.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('partner_accounts')
        .update({
          capital_request_type: requestType,
          capital_request_amount: numAmount,
          capital_request_date: new Date().toISOString(),
          capital_request_status: 'pending',
        })
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: "Request Submitted",
        description: `Your ${requestType} request for $${numAmount.toLocaleString()} has been submitted. The fund manager will review it shortly.`,
      });

      setAmount('');
      setRequestType('deposit');
      onRequestComplete();
      onClose();
    } catch (error: any) {
      console.error('Error submitting capital request:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-delphi-dark border-delphi-gray/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-delphi-light">
            Capital Request
          </DialogTitle>
          <DialogDescription className="text-delphi-light/60">
            Request a deposit or withdrawal from your account.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Request Type Selection */}
          <div className="space-y-2">
            <Label className="text-delphi-light">Request Type</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={requestType === 'deposit' ? 'default' : 'outline'}
                className={requestType === 'deposit' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/10'}
                onClick={() => setRequestType('deposit')}
              >
                <ArrowDownCircle size={16} className="mr-2" />
                Deposit
              </Button>
              <Button
                type="button"
                variant={requestType === 'withdrawal' ? 'default' : 'outline'}
                className={requestType === 'withdrawal' 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/10'}
                onClick={() => setRequestType('withdrawal')}
              >
                <ArrowUpCircle size={16} className="mr-2" />
                Withdrawal
              </Button>
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-delphi-light">Amount (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-delphi-light/50">$</span>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light pl-7"
                placeholder="0.00"
              />
            </div>
            {requestType === 'withdrawal' && (
              <p className="text-xs text-delphi-light/50">
                Maximum available: ${currentValue.toLocaleString()}
              </p>
            )}
          </div>

          {/* Info Text */}
          <div className="p-3 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg">
            <p className="text-sm text-delphi-light/70">
              {requestType === 'deposit' 
                ? 'After submitting, you will receive instructions from the fund manager on how to complete your deposit.'
                : 'Withdrawal requests are typically processed within 5-10 business days. The fund manager will contact you to confirm the details.'
              }
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/10"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className={requestType === 'deposit' 
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-red-600 hover:bg-red-700 text-white'}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              `Submit ${requestType === 'deposit' ? 'Deposit' : 'Withdrawal'} Request`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CapitalRequestModal;
